import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import { isSameDay, startOfDay } from './calendarUtils';
import { toDate } from '../../utils/dates';

/**
 * Two separate controls — a date picker and a time picker — writing to a single
 * Date. Bounds are split accordingly: the date picker only ever sees whole days,
 * while the time picker is constrained only on the boundary day itself.
 */
const DateTimeField = ({
    value,
    onChange,
    step = 15,
    minDate,
    maxDate,
    dateFormat = 'dd MMM yyyy',
    disabled = false,
    invalid = false,
    clearable = false,
    size = 'md',
    className = '',
    datePlaceholder = 'Select date',
    timePlaceholder = '--:--',
}) => {
    const date = toDate(value);
    const min = toDate(minDate);
    const max = toDate(maxDate);

    const timeMin = min && date && isSameDay(date, min) ? min : null;
    const timeMax = max && date && isSameDay(date, max) ? max : null;

    // Picking a boundary day can leave the time out of bounds — pull it back in
    // rather than emitting a value the caller would have to reject.
    const clampDateTime = (next) => {
        if (!next) return next;
        if (min && next < min) return new Date(min);
        if (max && next > max) return new Date(max);
        return next;
    };

    return (
        <div className={`dt-datetime ${className}`.trim()}>
            <DatePicker
                value={date}
                onChange={next => onChange(next ? clampDateTime(next) : null)}
                placeholder={datePlaceholder}
                format={dateFormat}
                minDate={min ? startOfDay(min) : undefined}
                maxDate={max ? startOfDay(max) : undefined}
                disabled={disabled}
                invalid={invalid}
                clearable={clearable}
                size={size}
                className="dt-datetime__date"
                ariaLabel="Date"
            />
            <TimePicker
                value={date}
                onChange={next => onChange(clampDateTime(next))}
                step={step}
                minTime={timeMin}
                maxTime={timeMax}
                placeholder={timePlaceholder}
                disabled={disabled || !date}
                invalid={invalid}
                size={size}
                className="dt-datetime__time"
                ariaLabel="Time"
            />
        </div>
    );
};

export default DateTimeField;
